import React, { type PropsWithChildren, useCallback } from 'react';
import { Box } from '@mui/material';
import { trackEvent } from '@surfline/web-common';
import { useMount } from 'react-use';

import { fetchSwellEvents } from 'actions/swellEvents';
import ForecastHeader from 'components/ForecastHeader';
import { PageTypes } from 'components/ForecastHeader/hooks/usePageDetails';
import SwellEventCardContainer from 'components/SwellEventCardContainer/SwellEventCardContainer';
import { getSpotDetails, getSpotReportForecast } from 'selectors/spot';
import getSwellEvents from 'selectors/swellEvents';
import { getSubregionOverviewData } from 'selectors/subregion';
import { useAppDispatch, useAppSelector } from 'stores/hooks';
import type { SwellEvent } from 'types/swellEvents';

const SpotGuidePageContainer: React.FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useAppDispatch();
  const spot = useAppSelector(getSpotDetails);
  const spotReportViewData = useAppSelector(getSpotReportForecast);
  const subregionOverview = useAppSelector(getSubregionOverviewData);
  const swellEvents = useAppSelector(getSwellEvents);
  const subregionId = spot && spot.subregion ? spot.subregion._id : null;

  const onClickSwellEventCard = useCallback(
    (swellEvent: SwellEvent) => {
      trackEvent('Clicked Swell Alert Card', {
        title: swellEvent.name,
        contentType: 'Swell Alert',
        basins: swellEvent.basins.join(','),
        locationCategory: 'Spot Page - Top',
        destinationUrl: swellEvent.permalink,
        subregionId,
      });
    },
    [subregionId],
  );

  useMount(() => {
    if (spot) {
      dispatch(fetchSwellEvents(spot._id, 'spot'));
    }
  });

  return (
    <Box>
      <section className="sl-section-container">
        {swellEvents.length ? (
          <SwellEventCardContainer onClickCard={onClickSwellEventCard} events={swellEvents} />
        ) : null}
        <ForecastHeader
          conditions={spotReportViewData?.conditions?.value}
          hasLiveCam={spot?.cameras?.length > 0}
          pageTitle={`${spot?.name} Surf Guide`}
          pageType={PageTypes.SPOT_GUIDE}
          spot={spot}
          subregionOverview={subregionOverview}
        />
      </section>
      {children}
    </Box>
  );
};

export default SpotGuidePageContainer;
